/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 12 2024
 **/

import { atom, useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";

import { EventHandler } from "@rfice/eventhandler";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { SidebarID } from "@/frameworks/core/commons";
import { Properties } from "@/frameworks/libraries/properties";
import { useSidebarActions } from "../useSidebar";
import { useRoomViewActions } from "../useRoomView";
import { usePersistActions } from "../../persistence";

type FullSizeChatUIType = {
	chatType: ChatServices.RoomType;
	channelID: string;
	isReady: boolean;
	spaceID?: string;
	ignoreUpdate?: boolean;
	activityMessageID?: string;
};

const FullSizeChatUIAtom = atom<FullSizeChatUIType | null>(null);

export default function useFullSizeChatManager() {
	const sidebarActions = useSidebarActions();
	const { updateRoomView } = useRoomViewActions();
	const { chats } = usePersistActions();

	const show = useAtomCallback(
		(_, set, args: FullSizeChatUIType) => {
			set(FullSizeChatUIAtom, args);
			if (!args.ignoreUpdate) {
				if (!args.channelID) return;
				updateRoomView({ roomId: args.channelID, deviceKey: Properties.browserTabID, view: true });
			}
			const sidebarId = args?.activityMessageID ? SidebarID.ActivityFullSizeChat : SidebarID.FullSizeChat;
			sidebarActions.show(sidebarId);
		},
		{
			store: JotaiStore
		}
	);

	const set = useAtomCallback(
		(get, set, args: Partial<FullSizeChatUIType>) => {
			const data = get(FullSizeChatUIAtom);
			if (!data || !args) return;
			set(FullSizeChatUIAtom, {
				...data,
				...args
			});
		},
		{
			store: JotaiStore
		}
	);

	const hide = useAtomCallback(
		(get, set, ignoreUpdate: boolean = false) => {
			const data = get(FullSizeChatUIAtom);
			if (!ignoreUpdate) {
				if (!data?.channelID) return;
				updateRoomView({
					roomId: data?.channelID as string,
					deviceKey: Properties.browserTabID,
					view: false
				});
			}
			EventHandler.fire("$Editor::Draft$", data?.channelID);
			const draft = chats.chat_draft(data?.channelID || "");
			if (draft) {
				chats.check_has_draft(data?.channelID || "", true);
			}
			set(FullSizeChatUIAtom, null);
			const sidebarId = data?.activityMessageID ? SidebarID.ActivityFullSizeChat : SidebarID.FullSizeChat;
			sidebarActions.hide(sidebarId);
		},
		{
			store: JotaiStore
		}
	);

	const get = useAtomCallback(
		(getAtom) => {
			return getAtom(FullSizeChatUIAtom);
		},
		{
			store: JotaiStore
		}
	);

	const fullSizeChatActions = { show, hide, get, set };

	function useFullSizeChatState() {
		return useAtomValue(FullSizeChatUIAtom);
	}

	return { fullSizeChatActions, useFullSizeChatState };
}
